





import React, { useState } from "react";
import GrievanceCard from "./GrievanceCard";
import GrievanceStatusModal from "./GrievanceStatusModal";
import { updateGrievanceStatusApi } from "../../../app/api";
import toast from "react-hot-toast";
import { Filter, Search, Plus } from "lucide-react";

export default function AdminGrievanceList({ grievances, onRefresh, onAddNew }) {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const handleAction = (id, status) => {
    const grievance = grievances.find(g => g.id === id);
    if (status === "in_progress") {
      updateStatus(id, status, "Starting review...");
    } else {
      setSelectedGrievance(grievance);
      setNewStatus(status);
    }
  };

  const updateStatus = async (id, status, note) => {
    try {
      await updateGrievanceStatusApi(id, { status, admin_note: note });
      toast.success(`Status updated to ${status}`);
      onRefresh();
      setSelectedGrievance(null);
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const filteredGrievances = grievances.filter(g => {
    const matchesFilter = filter === "all" || g.status === filter;
    const matchesSearch =
      g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.id.toString().includes(searchTerm);

    return matchesFilter && matchesSearch;
  });

  const stats = {
    pending: grievances.filter(g => g.status === "pending").length,
    in_progress: grievances.filter(g => g.status === "in_progress").length,
    resolved: grievances.filter(g => g.status === "resolved").length,
    reopened: grievances.filter(g => g.status === "reopened").length,
  };

  return (
    <div className="max-w-7xl mx-auto ">
      <div className="relative overflow-hidden rounded-[32px] p-6 md:p-8 bg-gradient-to-br from-[#1e174a] to-[#0f0c29] text-white border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]">

        {/* Glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 blur-[100px]" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-600/20 blur-[100px]" />

        {/* HEADER */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <Filter className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Grievance Management</h1>
              <p className="text-white/60 text-sm">
                Manage and track employee issues
              </p>
            </div>
            <button
              onClick={onAddNew}
              className="ml-auto flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-indigo-500/25 hover:from-blue-500 hover:to-indigo-500 transition-all"
            >
              <Plus size={18} />
              New Complaint
            </button>
          </div>

          {/* STATS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Pending", count: stats.pending },
              { label: "Active", count: stats.in_progress },
              { label: "Resolved", count: stats.resolved },
              { label: "Reopened", count: stats.reopened },
            ].map((stat, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                <p className="text-2xl font-bold text-white">{stat.count}</p>
                <p className="text-xs text-white/60 uppercase">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* FILTERS */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/5 border border-white/10 backdrop-blur-xl p-4 rounded-2xl mb-6">

            <div className="flex items-center gap-2 overflow-x-auto">
              {["All", "Pending", "In_Progress", "Resolved", "Reopened", "Rejected"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                    filter === f
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                      : "bg-white/5 text-white/60 hover:bg-white/10"
                  }`}
                >
                  {f.replace("_", " ")}
                </button>
              ))}
            </div>

            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <input
                type="text"
                placeholder="Search..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:border-blue-500/50 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* LIST */}
          {filteredGrievances.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGrievances.map((grievance) => (
                <GrievanceCard
                  key={grievance.id}
                  grievance={grievance}
                  isAdmin
                  onAction={handleAction}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-white/50">
              No grievances found
            </div>
          )}
        </div>
      </div>

      {selectedGrievance && (
        <GrievanceStatusModal
          isOpen
          onClose={() => setSelectedGrievance(null)}
          onConfirm={(note) => updateStatus(selectedGrievance.id, newStatus, note)}
          title={selectedGrievance.title}
          newStatus={newStatus}
        />
      )}
    </div>
  );
}







// import React, { useState } from "react";
// import GrievanceCard from "./GrievanceCard";
// import GrievanceStatusModal from "./GrievanceStatusModal";
// import { updateGrievanceStatusApi } from "../../../app/api";
// import toast from "react-hot-toast";
// import { Filter, Search } from "lucide-react";

// export default function AdminGrievanceList({ grievances, onRefresh }) {
//   const [filter, setFilter] = useState("all");
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedGrievance, setSelectedGrievance] = useState(null);
//   const [newStatus, setNewStatus] = useState("");

//   const handleAction = (id, status) => {
//     const grievance = grievances.find(g => g.id === id);
//     if (status === 'in_progress') {
//       // Direct update for in_progress
//       updateStatus(id, status, "Starting review...");
//     } else {
//       // Modal for resolved/rejected
//       setSelectedGrievance(grievance);
//       setNewStatus(status);
//     }
//   };

//   const updateStatus = async (id, status, note) => {
//     try {
//       await updateGrievanceStatusApi(id, { status, admin_note: note });
//       toast.success(`Status updated to ${status}`);
//       onRefresh();
//       setSelectedGrievance(null);
//     } catch (err) {
//       toast.error(err.message || "Failed to update status");
//     }
//   };

//   const filteredGrievances = grievances.filter(g => {
//     const matchesFilter = filter === "all" || g.status === filter;
//     const matchesSearch = 
//       g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       g.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       g.id.toString().includes(searchTerm);
//     return matchesFilter && matchesSearch;
//   });

//   const stats = {
//     pending: grievances.filter(g => g.status === 'pending').length,
//     in_progress: grievances.filter(g => g.status === 'in_progress').length,
//     resolved: grievances.filter(g => g.status === 'resolved').length,
//     reopened: grievances.filter(g => g.status === 'reopened').length,
//   };

//   return (
//     <div className="space-y-6">
//       {/* Stats Quick View */}
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
//         {[
//           { label: "Pending", count: stats.pending, color: "text-amber-500", bg: "bg-amber-500/10" },
//           { label: "Active", count: stats.in_progress, color: "text-blue-500", bg: "bg-blue-500/10" },
//           { label: "Resolved", count: stats.resolved, color: "text-emerald-500", bg: "bg-emerald-500/10" },
//           { label: "Reopened", count: stats.reopened, color: "text-purple-500", bg: "bg-purple-500/10" },
//         ].map((stat, i) => (
//           <div key={i} className={`${stat.bg} p-4 rounded-2xl border border-white/5`}>
//             <p className={`text-2xl font-black ${stat.color}`}>{stat.count}</p>
//             <p className="text-xs font-bold text-white uppercase tracking-widest">{stat.label}</p>
//           </div>
//         ))}
//       </div>

//       {/* Filters & Search */}
//       <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-900/50 p-4 rounded-2xl border border-slate-800">
//         <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto no-scrollbar">
//           <Filter size={16} className="text-slate-500 shrink-0" />
//           {["all", "pending", "in_progress", "resolved", "reopened", "rejected"].map((f) => (
//             <button
//               key={f}
//               onClick={() => setFilter(f)}
//               className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
//                 filter === f 
//                   ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/25" 
//                   : "bg-slate-800 text-slate-400 hover:bg-slate-700"
//               }`}
//             >
//               {f.charAt(0).toUpperCase() + f.slice(1).replace('_', ' ')}
//             </button>
//           ))}
//         </div>

//         <div className="relative w-full md:w-64">
//           <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
//           <input
//             type="text"
//             placeholder="Search by title, name..."
//             className="w-full bg-slate-950/50 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/50 transition-colors"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>
//       </div>

//       {/* List */}
//       {filteredGrievances.length > 0 ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//           {filteredGrievances.map((grievance) => (
//             <GrievanceCard
//               key={grievance.id}
//               grievance={grievance}
//               isAdmin={true}
//               onAction={handleAction}
//             />
//           ))}
//         </div>
//       ) : (
//         <div className="text-center py-20 bg-slate-900/30 rounded-3xl border border-dashed border-slate-800">
//           <p className="text-slate-500 font-medium">No grievances found matching your criteria.</p>
//         </div>
//       )}

//       {/* Status Modal */}
//       {selectedGrievance && (
//         <GrievanceStatusModal
//           isOpen={!!selectedGrievance}
//           onClose={() => setSelectedGrievance(null)}
//           onConfirm={(note) => updateStatus(selectedGrievance.id, newStatus, note)}
//           title={selectedGrievance.title}
//           newStatus={newStatus}
//         />
//       )}
//     </div>
//   );
// }
